import { Component, computed, effect, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { TranslocoPipe } from '@jsverse/transloco';
import { ENVIRONMENT } from '../../../../environments/environment.interface';
import { InstitutionExtended, InstitutionPerson } from '../../../api/app-types/institution.app';
import { INSTITUTION_PERSON_SERVICE } from '../../../api/services/institution-person.service';
import { NOTIFICATION_SERVICE } from '../../../shared/notification/notification';
import { createErrorNotification } from '../../../shared/notification/notification.factory';
import { NotificationService } from '../../../shared/notification/notification.service';
import { PaginatedResponse } from '../../../shared/pagination/paginated-response';
import { InstitutionPersonsList } from '../institution-persons-list/institution-persons-list';
import { lastValueFrom } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { NewInstitutionPersonDialog } from '../new-institution-person-dialog/new-institution-person-dialog';
import { LvrHeading } from '../../../core/components/heading/heading';
import { LvrButton } from '../../../core/components/lvr-button/lvr-button';

@Component({
	selector: 'lvr-institution-person',
	templateUrl: './institution-persons.html',
	styleUrl: './institution-persons.scss',
	imports: [TranslocoPipe, InstitutionPersonsList, LvrHeading, LvrButton],
	providers: [
		{
			provide: NOTIFICATION_SERVICE,
			useClass: NotificationService,
		},
	],
})
export class InstitutionPersons {
	private readonly env = inject(ENVIRONMENT);
	private readonly route = inject(ActivatedRoute);
	private readonly personsService = inject(INSTITUTION_PERSON_SERVICE);
	private readonly notificationService = inject(NOTIFICATION_SERVICE);
	private readonly dialog = inject(MatDialog);

	private readonly data = toSignal(this.route.data);
	public readonly pageSize = signal(this.env.defaultPageSize);
	public readonly loading = signal(false);
	public readonly institution = computed(() => this.data()?.['institution'] as InstitutionExtended);
	public readonly personsPage = signal<PaginatedResponse<InstitutionPerson>>({
		entities: [],
		offset: 0,
		range: 0,
		totalEntities: 0,
	});
	private readonly initialPersonsPage = computed<PaginatedResponse<InstitutionPerson>>(() => {
		const data = this.data();
		return data && 'persons' in data
			? (data['persons'] as PaginatedResponse<InstitutionPerson>)
			: { entities: [], offset: 0, range: 0, totalEntities: 0 };
	});

	public constructor() {
		effect(() => {
			const page = this.initialPersonsPage();
			this.personsPage.set(page);
			this.pageSize.set(page.range || this.env.defaultPageSize);
		});
	}

	public async onPageRequested(event: { pageIndex: number; pageSize: number }) {
		this.loading.set(true);
		try {
			// Offset represents the page number, not items to skip
			// Example: page 0 = offset 0, page 1 = offset 1
			const offset = event.pageIndex;
			const range = event.pageSize;
			const updated = await this.personsService.getAll(this.institution().id, offset, range);
			this.personsPage.set(updated);
		} catch {
			this.notificationService.addNotification(createErrorNotification('Fehler beim Neuladen der Ansprechpartner'));
		} finally {
			this.loading.set(false);
		}
	}

	public async openNewInstitutionPersonDialog() {
		const dialogRef = this.dialog.open(NewInstitutionPersonDialog, {
			data: { institutionId: this.institution().id },
			width: '40vw',
		});
		await lastValueFrom(dialogRef.afterClosed());
	}
}
